import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import SublyApi from '../../../helpers/Api';
import { formatCurrency, formatDate } from '../shared/constants';
import Table from '../../../components/ui/table';
import Button from '../../../components/ui/button';
import Input from '../../../components/ui/input';

const SpendingTrackerDashboard = () => {
	const history = useHistory();
	const [clients, setClients] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [currentPage, setCurrentPage] = useState(1);
	const pageSize = 10;

	// Fetch users and their expense summaries
	useEffect(() => {
		const fetchClientsWithExpenses = async () => {
			try {
				setLoading(true);
				setError(null);

				// Build current month date range
				const now = new Date();
				const year = now.getFullYear();
				const month = now.getMonth();
				const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
				const lastDay = new Date(year, month + 1, 0).getDate();
				const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

				// Fetch all-time and current month summaries in parallel
				const [allTimeData, monthData] = await Promise.all([
					SublyApi.getAdminExpenseSummary(),
					SublyApi.getAdminExpenseSummary({ start_date: startDate, end_date: endDate }),
				]);

				// Index month totals by username for fast lookup
				const monthTotalsByUser = {};
				for (const user of monthData.summary) {
					monthTotalsByUser[user.username] = user.totalAmount || 0;
				}

				// Merge all-time totals with current month totals
				const clientsWithExpenses = allTimeData.summary.map(userSummary => ({
					username: userSummary.username,
					name: `${userSummary.firstName} ${userSummary.lastName}`,
					email: userSummary.email,
					totalExpensesMonth: monthTotalsByUser[userSummary.username] || 0,
					totalExpensesAllTime: userSummary.totalAmount || 0,
					lastUpdated: userSummary.latestExpenseDate || null,
				}));

				setClients(clientsWithExpenses);
				// Clear any previous errors on successful load
				setError(null);
			} catch (err) {
				console.error('Error fetching clients:', err);
				setError(err.message || 'Failed to load clients');
			} finally {
				setLoading(false);
			}
		};

		fetchClientsWithExpenses();
	}, []);

	// Filter clients by search term
	const filteredClients = clients.filter((client) =>
		client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
		client.email.toLowerCase().includes(searchTerm.toLowerCase())
	);

	// Pagination
	const totalPages = Math.ceil(filteredClients.length / pageSize);
	const startIndex = (currentPage - 1) * pageSize;
	const paginatedClients = filteredClients.slice(startIndex, startIndex + pageSize);

	// Reset to page 1 when search changes
	const handleSearchChange = (e) => {
		setSearchTerm(e.target.value);
		setCurrentPage(1);
	};

	const handleViewClient = (username) => {
		history.push(`/admin/spending-tracker/client/${username}`);
	};

	// Calculate summary stats
	const totalClients = clients.length;
	const totalSpentMonth = clients.reduce((sum, client) => sum + client.totalExpensesMonth, 0);
	const totalSpentAllTime = clients.reduce((sum, client) => sum + client.totalExpensesAllTime, 0);

	return (
		<div className='min-h-screen bg-gray-50 p-6'>
			<div className='max-w-7xl mx-auto'>
				{/* Header */}
				<div className='mb-8'>
					<h1 className='text-4xl font-glacial font-bold text-clientBlack mb-2'>
						Spending Tracker
					</h1>
					<p className='text-gray-600 font-montserrat'>
						Manage marketing expenses for all clients
					</p>
				</div>

				{/* Error Message */}
				{error && (
					<div className='mb-6 bg-red-50 border border-red-200 rounded-lg p-4'>
						<p className='text-red-800 font-montserrat'>{error}</p>
					</div>
				)}

				{/* Search Bar */}
				<div className='mb-6'>
					<Input
						type='text'
						placeholder='Search clients by name or email...'
						value={searchTerm}
						onChange={handleSearchChange}
						className='max-w-md'
						disabled={loading}
					/>
				</div>

				{/* Loading State */}
				{loading ? (
					<div className='bg-white rounded-lg border border-gray-200 p-12 text-center'>
						<p className='text-gray-500 font-montserrat'>Loading clients...</p>
					</div>
				) : (
					<>
						{/* Clients Table */}
						<div className='bg-white rounded-lg border border-gray-200 overflow-hidden'>
							<Table>
								<Table.Header>
									<Table.Row>
										<Table.Head className='font-montserrat-medium'>
											Client Name
										</Table.Head>
										<Table.Head className='font-montserrat-medium'>
											Email
										</Table.Head>
										<Table.Head className='font-montserrat-medium text-right'>
											Total (Current Month)
										</Table.Head>
										<Table.Head className='font-montserrat-medium text-right'>
											Total (All Time)
										</Table.Head>
										<Table.Head className='font-montserrat-medium'>
											Last Updated
										</Table.Head>
										<Table.Head className='font-montserrat-medium text-center'>
											Actions
										</Table.Head>
									</Table.Row>
								</Table.Header>
								<Table.Body>
									{paginatedClients.length > 0 ? (
										paginatedClients.map((client) => (
											<Table.Row key={client.username} className='hover:bg-gray-50'>
												<Table.Cell className='font-montserrat font-medium text-clientBlack'>
													{client.name}
												</Table.Cell>
												<Table.Cell className='font-montserrat text-gray-600'>
													{client.email}
												</Table.Cell>
												<Table.Cell className='font-montserrat-medium text-right text-clientBlack'>
													{formatCurrency(client.totalExpensesMonth)}
												</Table.Cell>
												<Table.Cell className='font-montserrat-medium text-right text-clientBlack'>
													{formatCurrency(client.totalExpensesAllTime)}
												</Table.Cell>
												<Table.Cell className='font-montserrat text-gray-600'>
													{client.lastUpdated ? formatDate(client.lastUpdated) : 'No expenses yet'}
												</Table.Cell>
												<Table.Cell className='text-center'>
													<Button
														onClick={() => handleViewClient(client.username)}
														className='bg-primary hover:bg-primary/90 text-white font-montserrat'>
														View Details
													</Button>
												</Table.Cell>
											</Table.Row>
										))
									) : (
										<Table.Row>
											<Table.Cell
												colSpan={6}
												className='text-center py-8 text-gray-500 font-montserrat'>
												{searchTerm ? 'No clients match your search' : 'No clients found'}
											</Table.Cell>
										</Table.Row>
									)}
								</Table.Body>
							</Table>

							{/* Pagination */}
							{totalPages > 1 && (
								<div className='border-t border-gray-200 p-4 flex items-center justify-between'>
									<p className='text-sm font-montserrat text-gray-600'>
										Page {currentPage} of {totalPages}
									</p>
									<div className='flex gap-2'>
										<Button
											onClick={() => setCurrentPage(currentPage - 1)}
											disabled={currentPage === 1}
											variant='outline'
											size='sm'>
											Previous
										</Button>
										<Button
											onClick={() => setCurrentPage(currentPage + 1)}
											disabled={currentPage === totalPages}
											variant='outline'
											size='sm'>
											Next
										</Button>
									</div>
								</div>
							)}
						</div>

						{/* Summary Stats */}
						<div className='mt-6 grid grid-cols-1 md:grid-cols-3 gap-4'>
							<div className='bg-white rounded-lg border border-gray-200 p-6'>
								<p className='text-sm font-montserrat text-gray-600 mb-1'>
									Total Clients
								</p>
								<p className='text-3xl font-glacial font-bold text-clientBlack'>
									{totalClients}
								</p>
							</div>
							<div className='bg-white rounded-lg border border-gray-200 p-6'>
								<p className='text-sm font-montserrat text-gray-600 mb-1'>
									Total Spent (This Month)
								</p>
								<p className='text-3xl font-glacial font-bold text-primary'>
									{formatCurrency(totalSpentMonth)}
								</p>
							</div>
							<div className='bg-white rounded-lg border border-gray-200 p-6'>
								<p className='text-sm font-montserrat text-gray-600 mb-1'>
									Total Spent (All Time)
								</p>
								<p className='text-3xl font-glacial font-bold text-clientBlack'>
									{formatCurrency(totalSpentAllTime)}
								</p>
							</div>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default SpendingTrackerDashboard;
